import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ContractRequirement {
  id: string;
  contract_id: string;
  requirement_type: string;
  category?: string;
  requirement_text: string;
  is_mandatory: boolean;
  priority: string;
  is_evaluation_factor: boolean;
  evaluation_weight?: number;
  source_section?: string;
  keywords?: string[];
  match_score?: number;
  gap_analysis?: string;
}

export interface ContractSOW {
  id: string;
  contract_id: string;
  background?: string;
  scope?: string;
  objectives?: string[];
  tasks?: any[];
  deliverables?: any[];
  performance_standards?: string;
  period_of_performance?: string;
  place_of_performance?: string;
  clins?: any[];
}

export interface EvaluationCriteria {
  id: string;
  contract_id: string;
  evaluation_method?: string;
  evaluation_factors?: any[];
  technical_factors?: any[];
  past_performance_factors?: any[];
  price_factors?: any[];
  page_limits?: any;
  format_requirements?: string;
  submission_instructions?: string;
}

export interface DocumentAnalysisResult {
  requirements: ContractRequirement[];
  sow: ContractSOW | null;
  evaluation: EvaluationCriteria | null;
  summary: {
    summary: string;
    method: string;
    documentsProcessed?: number;
    hasSOW: boolean;
    hasEvaluation: boolean;
    evaluationMethod?: string;
  } | null;
  isAnalyzed: boolean;
  requirementsCount: number;
}

export function useDocumentAnalysis() {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing analysis for a contract
  const fetchAnalysis = useCallback(async (contractId: string): Promise<DocumentAnalysisResult> => {
    setLoading(true);
    setError(null);

    try {
      // Fetch contract analysis status
      const { data: contract, error: contractError } = await supabase
        .from('sam_contracts')
        .select('documents_analyzed, requirements_count, analysis_summary')
        .eq('notice_id', contractId)
        .single();

      if (contractError) throw contractError;

      // Fetch requirements
      const { data: requirements, error: reqError } = await supabase
        .from('contract_requirements')
        .select('*')
        .eq('contract_id', contractId)
        .order('priority', { ascending: true });

      if (reqError) throw reqError;

      // Fetch SOW (may not exist yet)
      const { data: sow } = await supabase
        .from('contract_sow')
        .select('*')
        .eq('contract_id', contractId)
        .maybeSingle();

      // Fetch evaluation criteria (may not exist yet)
      const { data: evaluation } = await supabase
        .from('contract_evaluation_criteria')
        .select('*')
        .eq('contract_id', contractId)
        .maybeSingle();

      return {
        requirements: requirements || [],
        sow: sow || null,
        evaluation: evaluation || null,
        summary: contract?.analysis_summary || null,
        isAnalyzed: contract?.documents_analyzed || false,
        requirementsCount: contract?.requirements_count || 0
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analysis';
      setError(message);
      return {
        requirements: [],
        sow: null,
        evaluation: null,
        summary: null,
        isAnalyzed: false,
        requirementsCount: 0
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger document analysis for a contract
  const analyzeDocuments = useCallback(async (
    contractId: string,
    forceReanalysis: boolean = false
  ): Promise<{ success: boolean; documentsProcessed: number; requirementsExtracted: number; error?: string }> => {
    setAnalyzing(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-contract-documents', {
        body: { contractId, forceReanalysis }
      });

      if (fnError) throw fnError;

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
      return { success: false, documentsProcessed: 0, requirementsExtracted: 0, error: message };
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Get requirements grouped by type
  const getGroupedRequirements = useCallback((requirements: ContractRequirement[]) => {
    const grouped: Record<string, ContractRequirement[]> = {};

    for (const req of requirements) {
      const type = req.requirement_type || 'other';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(req);
    }

    // Sort each group by priority
    const priorityOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4 };
    for (const type of Object.keys(grouped)) {
      grouped[type].sort((a, b) =>
        (priorityOrder[a.priority] || 5) - (priorityOrder[b.priority] || 5)
      );
    }

    return grouped;
  }, []);

  // Get mandatory requirements only
  const getMandatoryRequirements = useCallback((requirements: ContractRequirement[]) => {
    return requirements.filter(req => req.is_mandatory);
  }, []);

  // Get evaluation factors
  const getEvaluationFactors = useCallback((requirements: ContractRequirement[]) => {
    return requirements.filter(req => req.is_evaluation_factor);
  }, []);

  return {
    loading,
    analyzing,
    error,
    fetchAnalysis,
    analyzeDocuments,
    getGroupedRequirements,
    getMandatoryRequirements,
    getEvaluationFactors
  };
}
